import { useState, useEffect, useRef, useCallback } from "react";

export const useStickyTableHeader = (rows = []) => {
    const [isSticky, setIsSticky] = useState(false);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [headerPosition, setHeaderPosition] = useState({ left: 0, width: 0 });

    const headerRef = useRef(null);
    const tableRef = useRef(null);
    const containerRef = useRef(null);
    const observerRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!headerRef.current) return;

        const table = headerRef.current.closest("table");
        if (table) {
            tableRef.current = table;
        }

        if (!containerRef.current) {
            let element = headerRef.current.parentElement;
            while (element && element !== document.body) {
                const style = window.getComputedStyle(element);
                if (style.overflowX === "auto" || style.overflowX === "scroll") {
                    containerRef.current = element;
                    break;
                }
                element = element.parentElement;
            }
        }

        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setHeaderPosition({ left: rect.left, width: rect.width });
        }
    }, []);

    const checkStickyState = useCallback(() => {
        if (!headerRef.current || !containerRef.current) return;

        const headerRect = headerRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        const headerAboveViewport = headerRect.top < 0;

        const containerInViewport = containerRect.top < window.innerHeight && containerRect.bottom > 0;

        const headerWithinContainer = containerRect.bottom > 0;

        const shouldBeSticky = headerAboveViewport && containerInViewport && headerWithinContainer;

        setIsSticky(shouldBeSticky);

        if (shouldBeSticky) {
            updatePosition();
        }
    }, [updatePosition]);

   useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return;

    updatePosition();

    observerRef.current = new IntersectionObserver(
        () => {  // Убираем неиспользуемый параметр [entry]
            checkStickyState();
        },
        { threshold: [0], rootMargin: "-1px 0px 0px 0px" }
    );

    observerRef.current.observe(headerElement);

    const handleScroll = (e) => {
        // Обновляем горизонтальный скролл
        if (containerRef.current && (e.target === containerRef.current || e.target.contains(containerRef.current))) {
            setScrollLeft(containerRef.current.scrollLeft);
        }

        // Проверяем sticky состояние при любом скролле
        checkStickyState();
    };

    const handleResize = () => {
        updatePosition();
        checkStickyState();
    };

    if (containerRef.current) {
        containerRef.current.addEventListener("scroll", handleScroll, { passive: true });
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        if (containerRef.current) {
            containerRef.current.removeEventListener("scroll", handleScroll);
        }
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
    };
}, [updatePosition, checkStickyState, rows]);


    return {
        headerRef,
        tableRef,
        isSticky,
        scrollLeft,
        headerPosition,
    };
};
